'use client';

import * as React from 'react';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';

import { cn } from '@repo/ui/lib/utils';

function TooltipProvider({
  delayDuration = 0,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Provider>) {
  return (
    <TooltipPrimitive.Provider
      data-slot="tooltip-provider"
      delayDuration={delayDuration}
      {...props}
    />
  );
}

function Tooltip({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Root>) {
  return (
    <TooltipProvider>
      <TooltipPrimitive.Root data-slot="tooltip" {...props} />
    </TooltipProvider>
  );
}

function TooltipTrigger({ ...props }: React.ComponentProps<typeof TooltipPrimitive.Trigger>) {
  return <TooltipPrimitive.Trigger data-slot="tooltip-trigger" {...props} />;
}

/**
 * Glass overlay, matching the dropdown surfaces rather than the stock
 * shadcn black chip. `surface-overlay` carries alpha like every rowa
 * surface, so the backdrop blur is what makes it legible over arbitrary
 * page content — never drop one without the other.
 */
function TooltipContent({
  className,
  sideOffset = 0,
  children,
  ...props
}: React.ComponentProps<typeof TooltipPrimitive.Content>) {
  return (
    <TooltipPrimitive.Portal>
      <TooltipPrimitive.Content
        data-slot="tooltip-content"
        sideOffset={sideOffset}
        className={cn(
          'border-rowa-border-glass-highlight bg-rowa-surface-overlay text-rowa-text-secondary shadow-rowa-panel rounded-rowa-lg backdrop-blur-rowa-overlay animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2 text-rowa-body-sm z-50 w-fit origin-(--radix-tooltip-content-transform-origin) border px-3 py-2 text-balance',
          className
        )}
        {...props}
      >
        {children}
        <TooltipPrimitive.Arrow className="bg-rowa-surface-overlay fill-rowa-surface-overlay border-rowa-border-glass-highlight z-50 size-2.5 translate-y-[calc(-50%_-_3px)] rotate-45 rounded-[2px] border-r border-b" />
      </TooltipPrimitive.Content>
    </TooltipPrimitive.Portal>
  );
}

/**
 * Lets a tooltip that carries real content open on a tap.
 *
 * Radix tooltips are hover and focus only: a touch never opens one, so an
 * explanation moved behind a hover would be out of reach on a phone. Spread
 * `rootProps` on the `Tooltip` and `triggerProps` on the trigger: a tap
 * toggles the panel, a tap anywhere else closes it, and mouse and keyboard
 * keep Radix's own hover and focus behaviour.
 */
function useTapTooltip() {
  const [open, setOpen] = React.useState(false);
  const touch = React.useRef(false);
  // Read at pointer down, before Radix's outside-press dismissal runs, so
  // a tap on an open trigger closes it rather than reopening it.
  const wasOpen = React.useRef(false);

  const triggerProps = {
    onPointerDown: (event: React.PointerEvent) => {
      touch.current = event.pointerType === 'touch' || event.pointerType === 'pen';
      if (!touch.current) return;
      wasOpen.current = open;
      // Radix closes the tooltip on press; the tap's click decides instead.
      event.preventDefault();
    },
    onPointerLeave: (event: React.PointerEvent) => {
      // A lifted finger "leaves" the trigger; that is not a close.
      if (event.pointerType === 'touch' || event.pointerType === 'pen') event.preventDefault();
    },
    onClick: (event: React.MouseEvent) => {
      if (!touch.current) return;
      touch.current = false;
      // Radix closes on click; a tap toggles.
      event.preventDefault();
      setOpen(!wasOpen.current);
    }
  };

  return { rootProps: { open, onOpenChange: setOpen }, triggerProps };
}

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider, useTapTooltip };
